<div class="flex flex-col w-full h-full">
  <h1 class="mb-8 text-light-300 text-3xl font-medium">
    Portfolio <span class="text-xl text-red-100">(Demo Mode)</span>
  </h1>

  <div class="flex flex-col gap-12 w-full h-full">
    <!-- Portfolio value -->
    <p-card class="w-full">
      @if(_portfolioService.isPortofolioLoading$ | async){
      <ng-container *ngTemplateOutlet="loadingSkeletonPortofolioValue" />
      } @else {
      <div class="flex flex-col gap-1">
        <div
          class="flex flex-row gap-2 items-center font-medium text-base text-light-300"
        >
          <h2>Portfolio value</h2>
          <svg-icon
            src="assets/icons/info.svg"
            svgClass="h-4 w-4"
            (click)="isTouchDevice && info.toggle($event)"
            (mouseenter)="!isTouchDevice && info.show($event)"
            (mouseleave)="!isTouchDevice && info.hide()"
          />

          <p-popover #info>
            <p class="w-56 text-light-0 text-base">
              The total value of your pending investments, cash balance, and all
              of your Properties (based on the latest valuation of the
              properties)
            </p>
          </p-popover>
        </div>

        <p class="font-bold text-3xl text-light-300">
          {{ portfolio?.totalValue | customCurrency : currency }}
        </p>
      </div>

      <app-portfolio-growth-chart
        [dates]="growthOverviewChartDates"
        [values]="portfolio?.growthOverviewChartValues || []"
      />
      }
    </p-card>

    <!-- Key financials -->
    <div class="flex flex-col gap-4">
      <h3 class="font-bold text-xl text-light-300">Key financials</h3>

      <div class="flex flex-col sm:flex-row gap-6">
        @if(_portfolioService.isPortofolioLoading$ | async){
        <ng-container *ngTemplateOutlet="loadingSkeletonfinanacialTiles" />
        <ng-container *ngTemplateOutlet="loadingSkeletonfinanacialTiles" />
        <ng-container *ngTemplateOutlet="loadingSkeletonfinanacialTiles" />

        } @else {
        <!-- Monthly income -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <svg-icon
                src="assets/icons/coins.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p class="font-semibold text-xl text-light-200">
                {{ portfolio?.monthlyIncome | customCurrency : currency }}
              </p>
            </div>

            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Monthly income
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && monthlyIncome.toggle($event)"
                  (mouseenter)="!isTouchDevice && monthlyIncome.show($event)"
                  (mouseleave)="!isTouchDevice && monthlyIncome.hide()"
                />

                <p-popover #monthlyIncome>
                  <p class="w-56 text-light-0 text-base">
                    This displays the most recent rental income deposited into
                    your Seedio's wallet. Please note that this figure may vary
                    due to various factors such as occupancy levels, temporary
                    rental pauses for maintenance or vacancy, changes in rental
                    agreements etc.
                  </p>
                </p-popover>
              </p>

              <p class="text-sm text-light-100">
                {{ portfolio?.financialPeriod | date : "MMMM YYYY" }}
              </p>
            </div>
          </div>
        </p-card>

        <!-- Total rental income -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <svg-icon
                src="assets/icons/money-up.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p class="font-semibold text-xl text-light-200">
                {{ portfolio?.totalRentalIncome | customCurrency : currency }}
              </p>
            </div>

            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Total rental income
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && rentalIncome.toggle($event)"
                  (mouseenter)="!isTouchDevice && rentalIncome.show($event)"
                  (mouseleave)="!isTouchDevice && rentalIncome.hide()"
                />

                <p-popover #rentalIncome>
                  <p class="w-56 text-light-0 text-base">
                    The total amount of monthly rent paid into your Seedio
                    wallet.
                  </p>
                </p-popover>
              </p>

              <p class="text-sm text-light-100">
                {{ portfolio?.financialPeriod | date : "MMMM YYYY" }}
              </p>
            </div>
          </div>
        </p-card>

        <!-- Total appreciation -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <svg-icon
                src="assets/icons/graph.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p class="font-semibold text-xl text-light-200">
                {{ portfolio?.totalAppreciation | customCurrency : currency }}
              </p>
            </div>

            <div
              class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
            >
              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Total appreciation
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && totalAppreciation.toggle($event)"
                  (mouseenter)="
                    !isTouchDevice && totalAppreciation.show($event)
                  "
                  (mouseleave)="!isTouchDevice && totalAppreciation.hide()"
                />

                <p-popover #totalAppreciation>
                  <p class="w-56 text-light-0 text-base">
                    Unrealised gains or losses from the latest valuations of the
                    owned properties in your portfolio
                  </p>
                </p-popover>
              </p>

              <p class="text-sm text-light-100">
                {{ portfolio?.financialPeriod | date : "MMMM YYYY" }}
              </p>
            </div>
          </div>
        </p-card>
        }
      </div>
    </div>

    <!-- Quick insights -->
    <div class="flex flex-col gap-4">
      <h3 class="font-bold text-xl text-light-300">Quick insights</h3>

      <div class="flex flex-col sm:flex-row gap-6">
        @if(_portfolioService.isPortofolioLoading$ | async){
        <ng-container *ngTemplateOutlet="loadingSkeletonInsightsTiles" />
        <ng-container *ngTemplateOutlet="loadingSkeletonInsightsTiles" />
        <ng-container *ngTemplateOutlet="loadingSkeletonInsightsTiles" />

        } @else {
        <!-- Number of properties -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-2 items-start justify-between">
              <svg-icon
                src="assets/icons/house-2.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Number of properties
              </p>

              <p class="font-semibold text-sm text-light-200">
                {{ portfolio?.numberOfInvestedProperties }}
              </p>
            </div>
          </div>
        </p-card>

        <!-- Occupancy rate -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-2 items-start justify-between">
              <svg-icon
                src="assets/icons/calendar-percentage.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Occupancy rate
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && occupancyRate.toggle($event)"
                  (mouseenter)="!isTouchDevice && occupancyRate.show($event)"
                  (mouseleave)="!isTouchDevice && occupancyRate.hide()"
                />

                <p-popover #occupancyRate>
                  <p class="w-56 text-light-0 text-base">
                    This is the percentage of your properties that are currently
                    generating income. In some cases a property will temporarily
                    stop generating income, and the reason will be detailed on
                    the property page in you Seedio portfolio.
                  </p>
                </p-popover>
              </p>

              <p class="font-semibold text-sm text-light-200">
                {{ portfolio?.occupancyRate }}%
              </p>
            </div>
          </div>
        </p-card>

        <!-- Annual rental yield -->
        <p-card class="w-full sm:basis-1/3">
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-2 items-start justify-between">
              <svg-icon
                src="assets/icons/house-2.svg"
                svgClass="h-6 w-6 text-blue-100"
              />

              <p
                class="flex flex-row gap-2 items-center text-sm text-light-100"
              >
                Annual rental yield
                <svg-icon
                  src="assets/icons/info.svg"
                  svgClass="h-4 w-4"
                  (click)="isTouchDevice && annualRentalYield.toggle($event)"
                  (mouseenter)="
                    !isTouchDevice && annualRentalYield.show($event)
                  "
                  (mouseleave)="!isTouchDevice && annualRentalYield.hide()"
                />

                <p-popover #annualRentalYield>
                  <p class="w-56 text-light-0 text-base">
                    Your portfolio's annualised property yield % is based on the
                    realised rental income that the property generated over the
                    past 12 months.
                  </p>
                </p-popover>
              </p>

              <p class="font-semibold text-sm text-light-200">
                {{ portfolio?.annualRentalYield }}%
              </p>
            </div>
          </div>
        </p-card>
        }
      </div>
    </div>

    <!-- My properties -->
    <div class="flex flex-col gap-4 pb-8">
      <h3 class="font-bold text-xl text-light-300">My properties</h3>

      <p-card class="w-full">
        @if(_portfolioService.isMyPropertiesLoading$ | async){
        <ng-container *ngTemplateOutlet="loadingSkeletonTable"></ng-container>
        } @else {
        <p-table
          [value]="myProperties"
          [scrollable]="true"
          scrollHeight="370px"
          stripedRows
          [tableStyle]="{ 'min-width': '50rem' }"
          size="large"
        >
          <ng-template #header>
            <tr>
              <th>Property</th>
              <th>Location</th>
              <th>Investment value</th>
              <th>Total rental income</th>
              <th>Status</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template #body let-properties>
            <tr>
              <td>
                <div class="flex flex-row gap-2">
                  <img
                    class="rounded-lg h-12 w-18 object-cover"
                    [src]="properties.property.images[0].url"
                    [alt]="properties.property.images[0].caption"
                  />

                  <div class="flex flex-col gap-1">
                    <h2
                      class="font-medium text-base text-light-300 truncate w-[200px]"
                    >
                      {{ properties.property.name }}
                    </h2>

                    <div class="flex flex-row gap-2">
                      @if (properties.property.category ===
                      categoryEnum.RESIDENTIAL) {
                      <div class="flex flex-row items-center gap-1">
                        <span class="font-semibold text-xs text-light-200">
                          {{ properties.property.category }}
                        </span>
                      </div>
                      }@else {
                      <div class="flex flex-row items-center gap-1">
                        <svg-icon
                          [src]="'assets/icons/bed.svg'"
                          svgClass="h-4 w-4 text-light-100"
                        />
                        <span class="font-semibold text-xs text-light-200">
                          {{ properties.property.numberOfBedrooms }}
                        </span>
                      </div>
                      }

                      <p-divider layout="vertical" styleClass="m-0! h-1!" />
                      <div class="flex flex-row items-center gap-1">
                        <svg-icon
                          [src]="'assets/icons/door.svg'"
                          svgClass="h-4 w-4 text-light-100"
                        />
                        <span class="font-semibold text-xs text-light-200">
                          #{{ properties.property.propertyNumber }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div class="flex flex-row items-center gap-1">
                  <svg-icon
                    [src]="
                      'assets/flags/' +
                      properties.property.country.icon +
                      '.svg'
                    "
                    svgClass="h-4 w-4"
                  />
                  <span class="text-base text-light-200">
                    {{ properties.property.city }}
                  </span>
                </div>
              </td>
              <td>
                <p class="text-base text-light-200">
                  {{ properties.investedValue | customCurrency : currency }}
                  <span class="text-green-100">
                    +{{ properties.investedValueGrowth | number : "1.2-2" }}
                  </span>
                </p>
              </td>
              <td>
                <p class="text-base text-light-200">
                  {{ properties.totalRentalIncome | customCurrency : currency }}
                </p>
              </td>
              <td>
                <div class="flex flex-row items-center gap-1">
                  <svg-icon
                    [src]="
                      'assets/icons/' + (properties.state | lowercase) + '.svg'
                    "
                    svgClass="h-4 w-4 text-light-100"
                  />
                  <span class="text-base text-light-200">
                    {{ properties.state }}
                  </span>
                </div>
              </td>
              <td>
                <p-button
                  severity="primary"
                  outlined="true"
                  label="Documents"
                  size="small"
                  (onClick)="
                    properties.showOwnershipDocumentsDialog =
                      !properties.showOwnershipDocumentsDialog
                  "
                />

                <p-dialog
                  header="Documents"
                  [modal]="true"
                  [(visible)]="properties.showOwnershipDocumentsDialog"
                  [style]="{ width: '25rem' }"
                >
                  <div class="flex flex-col gap-2">
                    @for (document of properties.documents; track $index) {
                    <a
                      class="flex flex-row gap-4 items-center justify-between p-4 border-[1.5px] text-light-300 border-light-150 rounded-lg cursor-pointer hover:bg-light-150 hover:text-blue-150 hover:underline"
                      [href]="document.url"
                      target="_blank"
                    >
                      <div
                        class="flex flex-row gap-2 items-center pointer-events-none"
                      >
                        <svg-icon
                          [src]="'assets/icons/' + document.type + '.svg'"
                          svgClass="h-6 w-6"
                        />
                        <p class="text-sm">
                          {{ document.name }}
                        </p>
                      </div>

                      <svg-icon
                        [src]="'assets/icons/download.svg'"
                        svgClass="h-5 w-5 text-blue-100"
                        (click)="
                          onDownloadDocument(
                            properties.documents[$index],
                            $event
                          )
                        "
                      />
                    </a>
                    }
                  </div>
                </p-dialog>
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="6">
                <div
                  class="flex flex-col gap-4 items-center justify-center text-base text-light-300 p-5"
                >
                  <svg-icon src="assets/icons/clock.svg" svgClass="h-8 w-8" />
                  <p>No investments found</p>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>

        <div class="px-4"></div>
        }
      </p-card>
    </div>
  </div>
</div>

<ng-template #loadingSkeletonPortofolioValue>
  <div class="flex flex-col gap-2">
    <p-skeleton width="400px" height="64px" />
    <p-skeleton width="100%" height="100px" />
  </div>
</ng-template>

<ng-template #loadingSkeletonTable>
  <p-table
    [value]="[1, 2, 3, 4, 5]"
    [scrollable]="true"
    scrollHeight="345px"
    stripedRows
    [tableStyle]="{ 'min-width': '50rem' }"
    size="large"
  >
    <ng-template #header>
      <tr>
        <th>Property</th>
        <th>Location</th>
        <th>Investment value</th>
        <th>Total rental income</th>
        <th>Status</th>
        <th></th>
      </tr>
    </ng-template>
    <ng-template #body let-transaction>
      <tr>
        <td>
          <div class="flex flex-row gap-1 items-center">
            <p-skeleton height="50px" width="80px" />
            <div class="flex flex-col gap-1 justify-center">
              <p-skeleton height="15px" width="120px" />
              <p-skeleton height="15px" width="120px" />
            </div>
          </div>
        </td>
        <td>
          <p-skeleton height="24px" width="80px" />
        </td>
        <td>
          <p-skeleton height="24px" width="150px" />
        </td>
        <td>
          <p-skeleton height="24px" width="100px" />
        </td>
        <td>
          <p-skeleton height="24px" width="80px" />
        </td>
        <td>
          <p-skeleton height="24px" width="80px" />
        </td>
      </tr>
    </ng-template>
  </p-table>
</ng-template>

<ng-template #loadingSkeletonfinanacialTiles>
  <p-card class="w-full basis-1/3">
    <div class="flex flex-col gap-2">
      <div
        class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
      >
        <p-skeleton height="24px" width="28px" />
        <p-skeleton height="24px" width="100px" />
      </div>

      <div
        class="flex flex-col xl:flex-row gap-2 items-start xl:items-center justify-between"
      >
        <p-skeleton height="24px" width="130px" />
        <p-skeleton height="24px" width="90px" />
      </div>
    </div>
  </p-card>
</ng-template>

<ng-template #loadingSkeletonInsightsTiles>
  <p-card class="w-full basis-1/3">
    <div class="flex flex-col gap-2">
      <p-skeleton height="24px" width="28px" />
      <p-skeleton height="24px" width="120px" />
      <p-skeleton height="24px" width="50px" />
    </div>
  </p-card>
</ng-template>
